<template>
  <div
    class="relative"
    :class="props.popoverClass"
  >
    <button
      ref="buttonRef"
      type="button"
      :class="[props.buttonClass, props.disabled ? 'opacity-50 cursor-not-allowed' : 'focus:outline-none h-full']"
      :disabled="props.disabled"
      @click.stop="openPopover"
    >
      <slot
        name="popoverButton"
        :open="isOpen"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        style="position: fixed; inset: 0; z-index: 10; pointer-events: none;"
      >
        <transition
          name="fade-slide"
          appear
        >
          <div
            v-show="isVisible"
            ref="panelRef"
            class="bg-white rounded-lg shadow-md py-1 border border-gray-200 w-56 pointer-events-auto overflow-y-auto custom-scrollbar-v2"
            :class="[props.panelClass, popoverPosition === 'top' ? 'bottom-full mb-2' : 'mt-2']"
            :style="popoverStyle"
            @click.stop="props.autoClose ? closePopover() : null"
          >
            <slot name="popoverOptions" />
          </div>
        </transition>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
  panelClass: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  popoverClass: { type: String, default: "" },
  buttonClass: { type: String, default: "" },
  autoClose: { type: Boolean, default: false },
  positionBottom: { type: Boolean, default: false },
});

const buttonRef = ref(null);
const panelRef = ref(null);
const popoverPosition = ref("bottom");
const isOpen = ref(false);
const isVisible = ref(false);
const popoverStyle = ref({});

const calculatePopoverPosition = () => {
  nextTick(() => {
    const button = buttonRef.value;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    popoverStyle.value = {
      position: 'fixed',
      zIndex: 50,
      right: `${window.innerWidth - rect.right}px`,
      top: popoverPosition.value === 'top' ? 'auto' : `${rect.bottom}px`,
      bottom: popoverPosition.value === 'top' ? `${window.innerHeight - rect.top}px` : 'auto',
      left: 'auto',
      transition: 'all 0.2s ease',
    };
  });
};

const openPopover = (event) => {
  event.stopPropagation();
  if (props.disabled) return;
  window.dispatchEvent(new CustomEvent('closeAllPopovers'));

  if (isOpen.value) {
    closePopover();
  } else {
    isOpen.value = true;
    nextTick(() => {
      calculatePopoverPosition();
      setTimeout(() => {
        isVisible.value = true;
        checkPopoverPosition();
      }, 10);
    });
  }
};

const closePopover = () => {
  isVisible.value = false;
  setTimeout(() => {
    isOpen.value = false;
  }, 250);
};

const checkPopoverPosition = () => {
  if (props.positionBottom) {
    popoverPosition.value = "bottom";
    calculatePopoverPosition();
    return;
  }

  nextTick(() => {
    const button = buttonRef.value;
    const panel = panelRef.value;
    if (!button || !panel) return;

    const rect = button.getBoundingClientRect();
    const panelHeight = panel.offsetHeight || 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    popoverPosition.value = (spaceBelow < panelHeight && spaceAbove > panelHeight) ? "top" : "bottom";
    calculatePopoverPosition();
  });
};

const handleClickOutside = (event) => {
  if (!isOpen.value) return;
  if (
    panelRef.value &&
    !panelRef.value.contains(event.target) &&
    !buttonRef.value.contains(event.target)
  ) {
    closePopover();
  }
};
const handleScroll = (event) => {
  const target = event.target;
  if (panelRef.value && panelRef.value.contains(target)) return;  
  if (isOpen.value) closePopover();
};

const handleResize = () => {
  if (isOpen.value) calculatePopoverPosition();
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('closeAllPopovers', () => {
    if (isOpen.value) closePopover();
  });
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('closeAllPopovers', closePopover);
});
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
